import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";

import styled, { css, keyframes } from "styled-components/macro";

import { useNetworkMetadata } from "Flocknote/apiHooks";
import { useIsTransparentBgRoute } from "Flocknote/state";

import {
  navSectionKeys,
  useIsInitialized,
  useNavBarIsFullyLoaded,
} from "NavBar/navBarStore";
import { useIsFullScreen } from "NavBar/stateHooks";

import CustomScrollbar from "UI/FnrCustomScrollbar/CustomScrollbar";
import FnrErrorBoundary from "UI/FnrErrorBoundary";
import FnrLoadingCarousel from "UI/FnrLoadingCarousel";

import { CATEGORY_COLORS, DEFAULT_COLORS } from "./scrollbarColors";

/**
 * AdminPageWrapper
 *
 * A layout wrapper component for admin pages that handles responsive width adjustments
 * based on the navigation bar state (collapsed/expanded). Provides a smooth transition
 * when the navigation enters or exits fullscreen mode.
 *
 * Features:
 * - Automatically adjusts content width when navigation bar toggles fullscreen mode
 * - Applies a 500ms delay when expanding to fullscreen to sync with navigation animations
 * - Optionally displays a decorative grid background pattern
 * - Includes custom scrollbar styling
 * - Accepts all standard div HTML attributes via rest props
 *
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - The content to be rendered within the wrapper
 * @param {boolean} [props.showGridBackground=false] - Whether to display the decorative grid background pattern. When false, displays a plain white background.
 * @param {boolean} [props.transparentBackground=false] - Whether to display the white background or not. Works best if you use TransparentPageFallback with suspense.
 * @param {boolean} [props.isLoading=false] - Whether to show the loading carousel overlay
 * @param {string} [props.navSectionKey] - Navigation section key for color theming (from navSectionKeys). Sets both scrollbar and loading colors based on section.
 * @param {string} [props.loadingColor] - Override for loading carousel color. Uses navSectionKey color if not provided.
 * @param {Object|string} [props.scrollbarColor] - Override for scrollbar colors. Uses navSectionKey colors if not provided.
 * @param {Object} [props.scrollRef] - Ref object for external scroll control (e.g., virtualization)
 *
 * @example
 * // Basic usage
 * <AdminPageWrapper>
 *   <YourPageContent />
 * </AdminPageWrapper>
 *
 * @example
 * // With navigation section theming
 * <AdminPageWrapper
 *   showGridBackground
 *   navSectionKey={navSectionKeys.database}
 * >
 *   <YourPageContent />
 * </AdminPageWrapper>
 */
function AdminPageWrapper(props) {
  return (
    <FnrErrorBoundary boundaryName="AdminPageWrapper">
      <AdminPageWrapperContent {...props} />
    </FnrErrorBoundary>
  );
}

AdminPageWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  showGridBackground: PropTypes.bool,
  transparentBackground: PropTypes.bool,
  isLoading: PropTypes.bool,
  loadingColor: PropTypes.string,
  navSectionKey: PropTypes.oneOf([
    navSectionKeys.database,
    navSectionKeys.giving,
    navSectionKeys.groups,
    navSectionKeys.signups,
    navSectionKeys.tools,
    "settings", // Settings doesn't have a navSectionKey but may be used
  ]),
  scrollbarColor: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  scrollRef: PropTypes.object, // RefObject for external scroll control
  disableScrollbar: PropTypes.bool,
  withoutInnerPadding: PropTypes.bool, // Reserved for future padding control
};

function AdminPageWrapperContent({
  children,
  showGridBackground = false,
  transparentBackground = false,
  isLoading = false,
  loadingColor,
  navSectionKey,
  scrollbarColor,
  scrollRef,
  disableScrollbar = false,
  withoutInnerPadding, // Destructure to prevent DOM warning (consumed by styled-components)
  ...rest
}) {
  const [{ isApp }] = useNetworkMetadata();
  const isFullScreen = useIsFullScreen();
  const isInitialized = useIsInitialized();
  const isFullyLoaded = useNavBarIsFullyLoaded();
  const isTransparentBgRoute = useIsTransparentBgRoute();
  const timeoutRef = useRef();

  const [fullWidth, setFullWidth] = useState();
  const shouldAnimate = isInitialized && isFullyLoaded;
  const centerInViewport = isTransparentBgRoute && !isFullyLoaded;

  // Resolve colors: explicit prop > navSectionKey > default
  const navSectionConfig = navSectionKey && CATEGORY_COLORS[navSectionKey];

  const resolvedScrollbarColor =
    scrollbarColor || navSectionConfig?.scrollbar || DEFAULT_COLORS.scrollbar;

  const resolvedLoadingColor =
    loadingColor || navSectionConfig?.loading || DEFAULT_COLORS.loading;

  useEffect(() => {
    clearTimeout(timeoutRef.current);
    if (isFullScreen)
      timeoutRef.current = setTimeout(() => {
        setFullWidth(true);
      }, 500);
    else setFullWidth(false);
  }, [isFullScreen]);

  return (
    <Wrapper
      $fullWidth={fullWidth}
      $isApp={isApp}
      $showGridBackground={showGridBackground}
      $transparentBackground={transparentBackground}
      $shouldAnimate={shouldAnimate}
      {...rest}
    >
      <LoadingOverlay $centerInViewport={centerInViewport}>
        <FnrLoadingCarousel
          show={isLoading}
          color={resolvedLoadingColor}
          whiteBackground={showGridBackground}
        />
      </LoadingOverlay>
      {disableScrollbar ? (
        children
      ) : (
        <SCustomScrollbar ref={scrollRef} colors={resolvedScrollbarColor}>
          {children}
        </SCustomScrollbar>
      )}
    </Wrapper>
  );
}

const SCustomScrollbar = styled(CustomScrollbar)`
  padding-bottom: var(--floating-toolbar-height, 0px);
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Wrapper = styled.div`
  position: relative;
  width: ${({ $fullWidth }) =>
    $fullWidth
      ? "100%"
      : "calc(100% - var(--nav-width) - var(--app-border-width))"};
  margin-left: ${({ $fullWidth }) => ($fullWidth ? "0px" : "var(--nav-width)")};
  margin-top: ${({ $fullWidth }) =>
    $fullWidth ? "0px" : "var(--app-border-width)"};
  margin-right: ${({ $fullWidth }) =>
    $fullWidth ? "0px" : "var(--app-border-width)"};
  margin-bottom: ${({ $fullWidth }) =>
    $fullWidth ? "0px" : "var(--app-border-width)"};
  border-radius: ${({ $fullWidth, $transparentBackground }) =>
    $fullWidth || $transparentBackground
      ? "0px"
      : "var(--content-border-radius)"};
  font-weight: 400;
  height: ${({ $fullWidth }) =>
    $fullWidth
      ? "var(--full-viewport-height, 100vh)"
      : "calc(var(--full-viewport-height, 100vh) - var(--app-border-width) * 2)"};
  overflow: hidden;
  padding-top: ${({ $isApp }) => ($isApp ? "env(safe-area-inset-top)" : "0")};
  transition: border-radius 300ms;
  ${({ $shouldAnimate }) =>
    $shouldAnimate
      ? css`
          animation: ${fadeIn} 0.3s ease-out both;
        `
      : "opacity: 0;"}
  ${({ $showGridBackground, $transparentBackground }) =>
    $transparentBackground
      ? `
        /* Match nav_background gradient, adjusted for content area offset */
        background-color: #182941;
        background-image: radial-gradient(calc(60vh) at calc(425px - var(--nav-width)) 60%, #3B4C64, #182941);
      `
      : $showGridBackground
      ? `
        background:
          linear-gradient(145deg, rgba(255, 255, 255, 0), rgba(246, 247, 248, 1)),
          url("/cdn/images/grid-gray.png"),
          linear-gradient(#ffffff, #ffffff);
        background-position: center center;
        background-size: auto, 40px 40px, auto;
      `
      : "background-color: #ffffff;"}
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  pointer-events: none;
  transform: ${({ $centerInViewport }) =>
    $centerInViewport
      ? "translateX(calc((var(--nav-width) - var(--app-border-width)) / -2))"
      : "translateX(0)"};
  transition: transform 0.5s ease;
`;

export default AdminPageWrapper;
